<template>
  <div class="flex flex-wrap gap-2">
    <div class="flex w-full justify-between items-center">
      <h2 class="text-primary-text text-h2">
        {{ patientListTitle }}
      </h2>

      <VTabs
        :model-value="currentDisplayMode"
        class="border-[#D5D7DA] font-semibold"
        bg-color="bg-active"
        color="primary-600"
        slider-color="white"
        height="40"
        inset
        @update:model-value="emit('update-display-mode', $event)"
      >
        <VTab :value="DisplayMode.COMPACT" class="font-semibold text-primary-600">
          <VIcon icon="list" class="mr-1.5" /> {{ t('List View') }}
        </VTab>
        <VTab :value="DisplayMode.EXPANDED" class="font-semibold text-primary-600">
          <VIcon icon="view_day" class="material-icons-outlined mr-1.5" />{{ t('Card View') }}
        </VTab>
      </VTabs>
    </div>

    <div class="flex flex-row justify-between items-center w-full mb-6">
      <div class="flex flex-row gap-2 items-center flex-wrap">
        <VBtn
          :active="false"
          color="primary"
          class="button-medium"
          variant="outlined"
          size="medium"
          prepend-icon="sort"
          data-testid="sort-button"
          @click.prevent
        >
          {{ getSortByChosenOptionLabel }}
          <VitalSignsSortingDialog />
        </VBtn>

        <VitalSignsPatientFilterButton />

        <VitalSignsFilterChips />
      </div>

      <VBtn
        v-if="canAddPatient"
        color="primary"
        variant="flat"
        class="button-medium"
        data-testid="add-patient-button"
        @click="emit('add-patient-click')"
      >
        {{ t('Add patient') }}
      </VBtn>
    </div>

    <AKIWidgets
      v-if="isAkiEnabled"
      :is-aki-loading="isAkiLoading"
      :selected="true"
      :total-cases="totalCases"
      :filtered-cases="filteredCases"
      :selected-widget="activeWidget"
      :very-high-risk-aki-patients="veryHighRiskAkiPatients"
      :high-risk-aki-patients="highRiskAkiPatients"
      @selected-widget-update="emit('widget-selected', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { DisplayMode } from '@/stores/models/VitalSignsDisplayMode'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import VitalSignsSortingDialog from '@/components/patient/components/molecules/VitalSignsSortingDialog.vue'
import VitalSignsPatientFilterButton from '@/components/patient/filter/VitalSignsPatientFilterButton.vue'
import VitalSignsFilterChips from '@/components/patient/filter/VitalSignFilterChips.vue'
import {
  getVitalSignsSortByOptions,
  type VitalSignsSortOption
} from '@/components/patient/models/VitalSignsSortByOptions'
import type { VitalSignsSortBy } from '@/stores/models/VitalSignsSortBy'
import { isEqual } from 'lodash'
import AKIWidgets from '@/components/patient/components/molecules/AKIWidgets.vue'
import { WidgetType } from '@/enums/WidgetType'

interface Props {
  currentDisplayMode: DisplayMode
  selectedSortValue: VitalSignsSortBy
  totalCases: number
  filteredCases: number
  veryHighRiskAkiPatients: number | null
  highRiskAkiPatients: number | null
  isAkiEnabled: boolean
  canAddPatient: boolean
  isAkiLoading?: boolean
  activeWidget: WidgetType
}

interface Emits {
  (e: 'update-display-mode', value: DisplayMode): void
  (e: 'add-patient-click'): void
  (e: 'widget-selected', value: WidgetType): void
}

const { t } = useI18n()

const { totalCases, filteredCases, isAkiEnabled, selectedSortValue, activeWidget } =
  defineProps<Props>()
const emit = defineEmits<Emits>()

const patientListTitle = computed<string>(() => {
  if (isAkiEnabled) {
    return t('Patient Overview')
  } else {
    if (totalCases === filteredCases) {
      return t('Patient Overview ({count})', { count: totalCases })
    }

    return t('Patient Overview ({filtered}/{received})', {
      filtered: filteredCases,
      received: totalCases
    })
  }
})

const getSortByChosenOptionLabel = computed<string>(() => {
  const options = getVitalSignsSortByOptions(t)

  const foundOption = options.find((option: VitalSignsSortOption) =>
    isEqual(option.value, selectedSortValue)
  )

  return foundOption?.label || t('Sort by...')
})
</script>

<style scoped></style>
